import * as THREE from 'three';
import { Racer, cloneRacerModel, type PodRacerAssemblyAssets } from './Racer';
import type { GhostRecording, GhostStartState } from '../timing/Ghost';
import { DEFAULT_RENDER_FPS_CAP } from './FrameTiming';
import type { RendererMode } from '../render/Renderer';

const RENDER_ORDER = 5;
const EDGE_ANGLE_DEG = 22;
const DEFAULT_RENDER_DT = 1 / DEFAULT_RENDER_FPS_CAP;

export type GhostTint = readonly [r: number, g: number, b: number];
export type GhostGroundSampler = (x: number, z: number) => number;
export type GhostVisualMode = 'ghost' | 'rabbit';
export type GhostModelDetail = 'proxy' | 'full';

/** Force-ghost blue. The blue channel is pushed well past the bloom
 *  threshold (1.6 linear) so the silhouette haloes; red and green stay low
 *  so the bloom doesn't wash the whole thing white. */
export const GHOST_TINT_PERSONAL_BEST: GhostTint = [0.25, 1.1, 6.5];
export const GHOST_TINT_TRAINING_RABBIT: GhostTint = [0.7, 4.6, 2.35];

/**
 * Replay-driven ghost ship. Owns a real {@link Racer} and re-runs the lap
 * by feeding the recorded inputs through the same {@link physicsStep} the
 * live racer uses — so banking, hover bob, and acceleration squat all
 * emerge from physics, not keyframes.
 */
export class GhostShip {
  readonly racer: Racer;
  private readonly modelDetail: GhostModelDetail;
  private modelLoaded = false;
  private modelTinted = false;
  private currentStep = 0;
  private elapsedMs = 0;
  private rec: GhostRecording | null = null;
  private groundSampler: GhostGroundSampler | null = null;
  private readonly proxy: THREE.Group;
  private readonly marker: THREE.Group;
  private markerClockSeconds = 0;
  private visualMode: GhostVisualMode;
  private tint: GhostTint;
  /** Cached racer.group for parent code that wants to add it to the scene. */
  readonly group: THREE.Group;

  constructor(
    tint: GhostTint = GHOST_TINT_PERSONAL_BEST,
    mode: GhostVisualMode = 'ghost',
    modelDetail: GhostModelDetail = 'proxy',
    rendererMode: RendererMode = 'webgl2',
  ) {
    this.tint = tint;
    this.visualMode = mode;
    this.modelDetail = modelDetail;
    this.racer = new Racer({ showPlaceholder: false, rendererMode });
    this.racer.showForwardArrow = false;
    this.racer.setVisualModelEnabled(modelDetail === 'full');
    this.group = this.racer.group;
    this.proxy = createGhostPodProxy();
    this.proxy.visible = modelDetail === 'proxy';
    this.group.add(this.proxy);
    this.marker = createPaceMarker();
    this.group.add(this.marker);
    this.group.visible = false;
    this.modelLoaded = modelDetail === 'proxy';
    this.modelTinted = modelDetail === 'proxy';
    this.applyVisualStyle();
  }

  async load(url: string): Promise<void> {
    if (this.modelDetail === 'proxy') {
      void url;
      this.modelLoaded = true;
      this.modelTinted = true;
      return;
    }
    await this.racer.load(url);
    this.applyGhostMaterial(this.racer.group);
    this.modelTinted = true;
    this.modelLoaded = true;
  }

  async loadPodRacerAssembly(assets: PodRacerAssemblyAssets): Promise<void> {
    if (this.modelDetail === 'proxy') {
      void assets;
      this.modelLoaded = true;
      this.modelTinted = true;
      return;
    }
    await this.racer.loadPodRacerAssembly(assets);
    this.applyGhostMaterial(this.racer.group);
    this.modelTinted = true;
    this.modelLoaded = true;
  }

  isLoaded(): boolean {
    return this.modelLoaded;
  }

  setVisible(v: boolean): void {
    this.group.visible = v;
  }

  setGroundSampler(fn: GhostGroundSampler | null): void {
    this.groundSampler = fn;
  }

  setVisualMode(mode: GhostVisualMode): void {
    this.visualMode = mode;
    this.tint = mode === 'rabbit' ? GHOST_TINT_TRAINING_RABBIT : GHOST_TINT_PERSONAL_BEST;
    this.applyVisualStyle();
  }

  /** Bind a recording; ghost will replay it from the start on next reset. */
  bind(rec: GhostRecording | null): void {
    this.rec = rec;
    this.resetReplay();
  }

  hasRecording(): boolean {
    return this.rec !== null;
  }

  /** Re-seat the ghost at the recording's timing-gate physics state. */
  resetReplay(): void {
    this.currentStep = 0;
    this.elapsedMs = 0;
    if (!this.rec) return;
    this.racer.setStart(this.rec.startState.x, this.rec.startState.z, this.rec.startState.heading);
    this.racer.state.velocity.set(
      this.rec.startState.velocityX,
      0,
      this.rec.startState.velocityZ,
    );
    this.racer.state.position.y = this.resolveHeight(this.rec.startState);
    this.racer.beginStep();
  }

  /** Has the ghost finished its recorded lap? */
  isFinished(): boolean {
    if (!this.rec) return true;
    return this.currentStep >= this.rec.states.length;
  }

  /** Lap-relative replay time. */
  getLapElapsedMs(): number | null {
    if (!this.rec) return null;
    const elapsed = this.currentStep * this.rec.stepDt * 1000;
    return Math.min(elapsed, this.rec.lapMs);
  }

  /**
   * Step the ghost to the next recorded post-correction state. Inputs are
   * retained on the recording for telemetry/debugging, but visual replay uses
   * the exact state samples that came out of the live game loop so wall
   * correction and other non-physics-step adjustments cannot desync the ghost.
   */
  step(dt: number): void {
    if (!this.rec) return;
    if (this.currentStep >= this.rec.states.length) return;
    const state = this.rec.states[this.currentStep];
    this.placeAtState(state);
    this.currentStep += 1;
    this.elapsedMs += dt * 1000;
  }

  /** Directly place the ghost at a physics state without advancing replay. */
  placeAtState(state: GhostStartState): void {
    this.racer.beginStep();
    this.racer.state.position.set(state.x, this.resolveHeight(state), state.z);
    this.racer.state.velocity.set(state.velocityX, 0, state.velocityZ);
    this.racer.state.heading = state.heading;
  }

  render(alpha: number, frameDt?: number, clockTimeSeconds?: number): void {
    const dt = frameDt ?? DEFAULT_RENDER_DT;
    if (typeof clockTimeSeconds === 'number' && Number.isFinite(clockTimeSeconds)) {
      this.markerClockSeconds = clockTimeSeconds;
    } else {
      this.markerClockSeconds += dt;
    }
    this.racer.render(alpha, frameDt, this.markerClockSeconds);
    this.updateProxy(dt, this.markerClockSeconds);
    this.updateMarker(dt, this.markerClockSeconds);
    if (this.modelDetail === 'full' && !this.modelTinted) {
      // Re-apply once a racer model has slotted in.
      this.applyGhostMaterial(this.racer.group);
      this.modelTinted = true;
    }
  }

  /**
   * Wireframe overlay over a faint translucent fill on every mesh in the
   * subtree. Idempotent — safe to call again after `Racer.load` swaps the
   * model in.
   */
  private applyGhostMaterial(root: THREE.Object3D): void {
    const fill = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.06,
      depthWrite: false,
      depthTest: true,
    });
    fill.color.setRGB(this.tint[0] * 0.4, this.tint[1] * 0.4, this.tint[2] * 0.4);

    const lines = new THREE.LineBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
      depthTest: true,
    });
    lines.color.setRGB(this.tint[0], this.tint[1], this.tint[2]);

    const meshes: THREE.Mesh[] = [];
    root.traverse((o) => {
      if (o.userData?.tstGhostMarker === true) return;
      if ((o as THREE.Mesh).isMesh) meshes.push(o as THREE.Mesh);
    });

    for (const mesh of meshes) {
      mesh.material = fill;
      mesh.castShadow = false;
      mesh.receiveShadow = false;
      mesh.renderOrder = RENDER_ORDER;
      // Strip any wireframe overlays added on a previous pass.
      const stale = mesh.children.filter((c) => c.userData?.tstGhostWire === true);
      for (const c of stale) mesh.remove(c);

      const geom = mesh.geometry;
      if (geom) {
        const edges = new THREE.EdgesGeometry(geom, EDGE_ANGLE_DEG);
        const wire = new THREE.LineSegments(edges, lines);
        wire.renderOrder = RENDER_ORDER + 1;
        wire.userData.tstGhostWire = true;
        mesh.add(wire);
      }
    }
  }

  private applyVisualStyle(): void {
    if (this.modelDetail === 'full') this.applyGhostMaterial(this.racer.group);
    const core = new THREE.Color().setRGB(this.tint[0], this.tint[1], this.tint[2]);
    const hot = this.visualMode === 'rabbit'
      ? new THREE.Color(0xd8ffe6)
      : new THREE.Color(0xd7f2ff);
    this.applyProxyMaterial(core, hot);
    this.marker.traverse((o) => {
      if (!((o as THREE.Mesh).isMesh || (o as THREE.LineSegments).isLineSegments)) return;
      const material = ((o as THREE.Mesh).material ?? (o as THREE.LineSegments).material) as THREE.Material | THREE.Material[] | undefined;
      const materials = Array.isArray(material) ? material : material ? [material] : [];
      for (const mat of materials) {
        mat.transparent = true;
        mat.depthWrite = false;
        mat.depthTest = true;
        if ('color' in mat && mat.color instanceof THREE.Color) {
          mat.color.copy(o.userData?.tstGhostHot === true ? hot : core);
        }
        if ('opacity' in mat) {
          mat.opacity = o.userData?.tstGhostHot === true ? 0.92 : 0.58;
        }
      }
    });
  }

  private applyProxyMaterial(core: THREE.Color, hot: THREE.Color): void {
    this.proxy.traverse((o) => {
      if (!(o instanceof THREE.Mesh || o instanceof THREE.Line)) return;
      const material = o.material as THREE.Material | THREE.Material[] | undefined;
      const materials = Array.isArray(material) ? material : material ? [material] : [];
      for (const mat of materials) {
        mat.transparent = true;
        mat.depthWrite = false;
        mat.depthTest = true;
        if ('color' in mat && mat.color instanceof THREE.Color) {
          mat.color.copy(o.userData?.tstGhostHot === true ? hot : core);
        }
        if ('opacity' in mat) {
          mat.opacity = o.userData?.tstGhostHot === true ? 0.82 : (o.userData?.tstGhostFill === true ? 0.13 : 0.72);
        }
      }
    });
  }

  private updateProxy(_dt: number, clockTimeSeconds: number): void {
    if (this.modelDetail !== 'proxy') return;
    this.proxy.visible = this.rec !== null;
    const t = clockTimeSeconds;
    this.proxy.position.y = 0.16 + Math.sin(t * 5.1) * 0.035;
    this.proxy.rotation.z = Math.sin(t * 3.2) * (this.visualMode === 'rabbit' ? 0.09 : 0.055);
    this.proxy.rotation.x = Math.sin(t * 2.4 + 0.6) * 0.025;
  }

  private updateMarker(dt: number, clockTimeSeconds: number): void {
    const t = clockTimeSeconds;
    this.marker.visible = this.rec !== null;
    this.marker.position.y = 3.1 + Math.sin(t * 4.2) * 0.12;
    this.marker.rotation.y += dt * (this.visualMode === 'rabbit' ? 2.4 : 1.6);
  }

  private resolveHeight(state: GhostStartState): number {
    // v4 recordings carry y; v3 records (loaded from older Supabase rows or
    // localStorage) lack it, so fall back to the live ground sampler.
    if (Number.isFinite(state.y)) return state.y;
    return this.groundSampler?.(state.x, state.z) ?? this.racer.state.position.y;
  }
}

/** Re-export so callers don't have to know about the underlying type. */
export { cloneRacerModel };

function createPaceMarker(): THREE.Group {
  const root = new THREE.Group();
  root.name = 'ghost-pace-marker';
  root.userData.tstGhostMarker = true;
  root.position.set(0, 3.1, 0);

  const ringMat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.58,
    depthWrite: false,
    depthTest: true,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
  });
  const ring = new THREE.Mesh(new THREE.TorusGeometry(0.72, 0.018, 8, 72), ringMat);
  ring.name = 'ghost-pace-ring';
  ring.rotation.x = Math.PI / 2;
  ring.renderOrder = RENDER_ORDER + 3;
  ring.userData.tstGhostMarker = true;
  root.add(ring);

  const pointerMat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.92,
    depthWrite: false,
    depthTest: true,
    blending: THREE.AdditiveBlending,
  });
  const pointer = new THREE.Mesh(new THREE.ConeGeometry(0.18, 0.5, 3), pointerMat);
  pointer.name = 'ghost-pace-pointer';
  pointer.userData.tstGhostMarker = true;
  pointer.userData.tstGhostHot = true;
  pointer.position.set(0, 0, 0.74);
  pointer.rotation.x = Math.PI / 2;
  pointer.renderOrder = RENDER_ORDER + 4;
  root.add(pointer);

  const stemMat = new THREE.LineBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.42,
    depthWrite: false,
    depthTest: true,
    blending: THREE.AdditiveBlending,
  });
  const stemGeom = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(0, -1.05, 0),
    new THREE.Vector3(0, -0.18, 0),
  ]);
  const stem = new THREE.LineSegments(stemGeom, stemMat);
  stem.name = 'ghost-pace-stem';
  stem.userData.tstGhostMarker = true;
  stem.renderOrder = RENDER_ORDER + 2;
  root.add(stem);

  return root;
}

function createGhostPodProxy(): THREE.Group {
  const root = new THREE.Group();
  root.name = 'ghost-pod-proxy';

  const fillMat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.13,
    depthWrite: false,
    depthTest: true,
    blending: THREE.AdditiveBlending,
  });
  const lineMat = new THREE.LineBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.72,
    depthWrite: false,
    depthTest: true,
    blending: THREE.AdditiveBlending,
  });
  const hotMat = new THREE.LineBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.82,
    depthWrite: false,
    depthTest: true,
    blending: THREE.AdditiveBlending,
  });

  const addMesh = (name: string, geometry: THREE.BufferGeometry, position: THREE.Vector3, rotation?: THREE.Euler): THREE.Mesh => {
    const mesh = new THREE.Mesh(geometry, fillMat);
    mesh.name = name;
    mesh.position.copy(position);
    if (rotation) mesh.rotation.copy(rotation);
    mesh.renderOrder = RENDER_ORDER;
    mesh.userData.tstGhostFill = true;
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    root.add(mesh);

    const edges = new THREE.LineSegments(new THREE.EdgesGeometry(geometry, EDGE_ANGLE_DEG), lineMat);
    edges.name = `${name}-edges`;
    edges.renderOrder = RENDER_ORDER + 1;
    edges.userData.tstGhostWire = true;
    mesh.add(edges);
    return mesh;
  };

  const zAxis = new THREE.Euler(Math.PI / 2, 0, 0);
  const engineGeom = new THREE.CylinderGeometry(0.34, 0.42, 1.55, 8, 1, false);
  addMesh('ghost-proxy-engine-l', engineGeom, new THREE.Vector3(-1.18, 0.42, -0.05), zAxis);
  addMesh('ghost-proxy-engine-r', engineGeom.clone(), new THREE.Vector3(1.18, 0.42, -0.05), zAxis);

  const cockpitGeom = new THREE.CylinderGeometry(0.24, 0.34, 0.95, 8, 1, false);
  addMesh('ghost-proxy-cockpit', cockpitGeom, new THREE.Vector3(0, 0.38, -1.08), zAxis);

  const noseGeom = new THREE.ConeGeometry(0.34, 0.62, 8);
  addMesh('ghost-proxy-nose', noseGeom, new THREE.Vector3(0, 0.38, -1.82), new THREE.Euler(-Math.PI / 2, 0, 0));

  const cablePoints = [
    new THREE.Vector3(-0.22, 0.36, -1.22), new THREE.Vector3(-1.18, 0.42, -0.22),
    new THREE.Vector3(0.22, 0.36, -1.22), new THREE.Vector3(1.18, 0.42, -0.22),
    new THREE.Vector3(-1.18, 0.42, 0.58), new THREE.Vector3(1.18, 0.42, 0.58),
  ];
  const cables = new THREE.LineSegments(new THREE.BufferGeometry().setFromPoints(cablePoints), lineMat);
  cables.name = 'ghost-proxy-cables';
  cables.renderOrder = RENDER_ORDER + 2;
  root.add(cables);

  const beam = new THREE.LineSegments(new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(-1.18, 0.42, 0.75),
    new THREE.Vector3(1.18, 0.42, 0.75),
  ]), hotMat);
  beam.name = 'ghost-proxy-energy-coupler';
  beam.renderOrder = RENDER_ORDER + 3;
  beam.userData.tstGhostHot = true;
  root.add(beam);

  return root;
}
